import { Button, useToast } from '@kanos/ui';
import { ProblemAlert, usePartyFields, useUnsavedChanges } from '@kanos/web-core';
import { useEffect, useState } from 'react';
import { useStateParty, type ContractingParty } from './intake-queries';

/**
 * What the contracting party states about itself (§ 11 Abs. 4 GwG, ADR-026): a person's data or
 * a company's, as the intake asks - a person also whether politically exposed (ADR-029). Every
 * field may stay empty; the firm checks what is stated. Input not saved yet holds the tab and is
 * told to the page (`onDirtyChange`): sending to the firm sends what is saved, so it waits.
 */
export function PartyForm({
  caseId,
  party,
  isOpen,
  onDirtyChange,
}: {
  caseId: string;
  party: ContractingParty;
  isOpen: boolean;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const save = useStateParty(caseId);
  const toast = useToast();
  const { input, fields } = usePartyFields(party.expects, party.current?.data, 'you');

  // What was saved last - the fields as they came, then each saved state.
  const [saved, setSaved] = useState(() => JSON.stringify(input()));
  const dirty = isOpen && JSON.stringify(input()) !== saved;
  useUnsavedChanges(dirty);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  return (
    <form
      className="grid gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        const body = input();
        save.mutate(body, {
          onSuccess: () => {
            setSaved(JSON.stringify(body));
            toast({ tone: 'success', title: 'Gespeichert' });
          },
        });
      }}
    >
      {fields}
      {save.isError && <ProblemAlert error={save.error} />}
      {isOpen && (
        <Button type="submit" className="justify-self-start" isPending={save.isPending}>
          Angaben speichern
        </Button>
      )}
    </form>
  );
}
